/**
 * 승인 대기 — 로그인은 됐지만 소속 브랜드가 아직 승인되지 않은 계정이 보는 화면. 라우트가 아니라 AppLayout 이 건다
 */
import { auth, signup } from '@geo-admin/shared/contract/api/index.ts';
import type { Me } from '@geo-admin/shared/contract/api/auth.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AuthCard, AuthShell, authField, HeroPanel, JourneySteps, StatusCircle } from '../components/AuthShell.tsx';
import { endImpersonation, ImpersonationBanner } from '../components/ImpersonationBanner.tsx';
import { HourglassIcon } from '../components/icons.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { Button } from '../components/ui/button.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { call } from '../lib/call.ts';
import { pathsFor } from '../lib/paths.ts';

export function PendingApproval({ me }: { me: Me }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [withdrawing, setWithdrawing] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const leave = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      qc.clear();
      await navigate({ to: pathsFor(me.role).login });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      {me.impersonator ? <ImpersonationBanner targetEmail={me.email} adminEmail={me.impersonator.email} /> : null}
      <AuthShell
        aside={
          <HeroPanel
            eyebrow={
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#8fb4ff] opacity-75 motion-safe:animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#8fb4ff]" />
                </span>
                승인 대기 중
              </>
            }
            title="승인을 준비하고 있습니다"
            lead={<span className="break-all">{me.email}</span>}
          >
            <JourneySteps current={1} />
          </HeroPanel>
        }
      >
        <AuthCard
          icon={
            <StatusCircle tone="wait">
              <HourglassIcon className="h-6 w-6" />
            </StatusCircle>
          }
          title="신청이 접수됐습니다"
          description="담당자가 내용을 확인하고 연락드릴 예정입니다. 문의사항은 아래 연락처를 참고해 주세요."
        >
          {me.impersonator ? (
            <Button variant="outline" size="lg" className="w-full" onClick={() => void endImpersonation()}>
              대리 종료
            </Button>
          ) : (
            <Button variant="outline" size="lg" className="w-full" onClick={() => void leave(() => call(auth.logout))}>
              로그아웃
            </Button>
          )}
        </AuthCard>

        <SupportContact />

        {me.impersonator ? null : (
          <div className="px-1 text-sm">
            {withdrawing ? (
              <div className="grid gap-3 rounded-2xl bg-white p-5 ring-1 ring-slate-200/70">
                <Label>
                  탈퇴하시려면 비밀번호를 입력해 주세요 — 남기신 개인정보는 즉시 파기됩니다
                  <Input
                    type="password"
                    autoComplete="current-password"
                    className={authField}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Label>
                <FieldError>{error}</FieldError>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setWithdrawing(false)}>
                    취소
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => void leave(() => call(signup.withdraw, { body: { password } }))}
                  >
                    탈퇴
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="text-slate-400 text-xs hover:text-slate-600 hover:underline"
                onClick={() => setWithdrawing(true)}
              >
                가입을 취소하고 탈퇴하기
              </button>
            )}
          </div>
        )}
      </AuthShell>
    </>
  );
}
